import { format, getDate, getMonth, getYear } from "date-fns";
import { useCallback, useState } from "react";

import { Sex, useCreateAppointmentViaWebMutation } from "~/graphql";

import { Fields } from "./types";

const formatDate = (time: string, date: Date) => {
  const year = getYear(date);
  const month = getMonth(date);
  const day = getDate(date);

  const FORMAT = "yyyy-MM-dd'T'HH:mm:ssxx";
  const hourMinute = time.split(":");

  return format(
    new Date(year, month, day, +hourMinute[0], +hourMinute[1]),
    FORMAT,
  );
};

export const useSendPrescriptions = () => {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createAppointmentViaWeb] = useCreateAppointmentViaWebMutation();

  const uploadPrescriptions = useCallback(async (prescriptions: File[]) => {
    const signedIds: string[] = [];

    await import("~/utils/upload").then(async (result) => {
      const promises = prescriptions.map((file) => {
        return result
          .upload(file)
          .then((result) => {
            signedIds.push(result.signedId);
          })
          .catch(() => {
            throw new Error("処方箋のアップロードに失敗しました");
          });
      });
      await Promise.all(promises);
    });

    return signedIds;
  }, []);

  const send = useCallback(
    async (values: Fields, pharmacyId?: string) => {
      setError(null);
      setIsSending(true);

      try {
        if (
          !pharmacyId ||
          !values.date ||
          !values.prescriptions ||
          values.prescriptions.length === 0
        ) {
          throw new Error();
        }

        const startAt = formatDate(values.startTime, values.date);
        const endAt = formatDate(values.endTime, values.date);

        const signedIds = await uploadPrescriptions(values.prescriptions);
        const answers = [
          values.answers.question1,
          values.answers.question2,
          values.answers.question3,
          values.answers.question4,
          values.answers.question5,
        ];

        await createAppointmentViaWeb({
          variables: {
            input: {
              answers,
              signedIds,
              startAt,
              endAt,
              familyName: values.familyName,
              givenName: values.givenName,
              phoneticFamilyName: values.phoneticFamilyName,
              phoneticGivenName: values.phoneticGivenName,
              mobilePhone: values.mobilePhone,
              birthDate: values.birthDate,
              sex: values.sex as Sex,
              organizationId: pharmacyId,
            },
          },
        });
        setIsSending(false);
      } catch (err) {
        setError(err?.message || "エラーが発生しました");
        setIsSending(false);
        throw new Error();
      }
    },
    [createAppointmentViaWeb, uploadPrescriptions],
  );

  return {
    isSending,
    error,
    send,
  };
};
